import { Icon } from "@design-system/Icon";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Sidebar } from "@design-system/Navigation/SideBar";
import { Text } from "@design-system/Typography/Text";
import { cn } from "@design-system/Utilities";
import { CsrdEsrs } from "@generated/client/graphql";
import { useAtomValue, useSetAtom } from "jotai";
import { useParams } from "react-router-dom";
import {
    activeDisclosureRequirementIdAtom,
    filterAtom,
    lastUrlUpdateByClickTimestampAtom,
    setScrollAnchorAtom,
} from "../../ctx";
import { useCsrdPillar, useCsrdPillarStats } from "../../data";

const useNavigate = () => {
    const setNavigationAnchor = useSetAtom(setScrollAnchorAtom);
    const setFirstDrActive = (esrs: CsrdEsrs) => {
        if (esrs?.disclosureRequirements?.length) {
            return setNavigationAnchor({
                drId: esrs.disclosureRequirements[0].cmsId,
            });
        }
    };
    return { setFirstDrActive };
};

export const PillarSidebar = () => {
    const { pillar_id } = useParams();

    const selectedDrId = useAtomValue(activeDisclosureRequirementIdAtom);

    const setNavigationAnchor = useSetAtom(setScrollAnchorAtom);

    const setLastUrlUpdateByClickTimestamp = useSetAtom(
        lastUrlUpdateByClickTimestampAtom,
    );

    const filter = useAtomValue(filterAtom);
    const { isPending, pillar } = useCsrdPillar(pillar_id, filter);
    const { disclosureRequirementStats } = useCsrdPillarStats(
        pillar_id,
        filter,
    );

    const { setFirstDrActive } = useNavigate();
    const isDisclosureRequirementValid = (drId: string) => {
        const stats = disclosureRequirementStats?.find(
            (dr) => dr.drCmsId === drId,
        )?.stats;
        return stats && stats.completed === stats.total;
    };
    const disclosureRequirementCompletionRatio = (drId: string) => {
        return disclosureRequirementStats?.find((dr) => dr.drCmsId === drId)
            ?.stats.percentage;
    };
    const isLastEsrs = (idx: number, esrss: CsrdEsrs[]) => {
        return !esrss || idx === esrss.length - 1;
    };

    const handleSelectedIdChange = (id: string) => {
        setLastUrlUpdateByClickTimestamp(new Date());
        setNavigationAnchor({ drId: id });
    };

    const handleSidebarGroupTitleClick = (esrs: CsrdEsrs) => {
        return () => {
            setLastUrlUpdateByClickTimestamp(new Date());
            setFirstDrActive(esrs);
        };
    };

    return (
        <Sidebar
            selectedId={selectedDrId}
            onSelectedIdChange={handleSelectedIdChange}
            className="w-[232px] sticky -top-3 max-h-screen overflow-y-auto"
            isLoading={isPending}
        >
            <Sidebar.Content className="gap-0">
                <>
                    {pillar?.esrss?.map((esrs, idx) =>
                        !esrs.disclosureRequirements.some(
                            (dr) => dr.datapoints.length > 0,
                        ) ? (
                            <Sidebar.GroupTitle key={idx} title={esrs.name} />
                        ) : (
                            <Sidebar.Group key={idx}>
                                <FlexCol w="full">
                                    <Sidebar.GroupTitle
                                        key={idx}
                                        title={esrs?.name}
                                        onClick={handleSidebarGroupTitleClick(
                                            esrs as CsrdEsrs,
                                        )}
                                    />
                                    {esrs.disclosureRequirements
                                        ?.filter(
                                            (dr) => dr.datapoints.length > 0,
                                        )
                                        .map((dr, idx2) => (
                                            <Sidebar.Item
                                                key={idx2}
                                                id={dr.cmsId}
                                            >
                                                {({ isSelected }) => (
                                                    <FlexRow
                                                        p="2"
                                                        alignItems="center"
                                                        justifyContent="between"
                                                        gap="3"
                                                        w="full"
                                                        className={cn(
                                                            "rounded-lg",
                                                            isSelected &&
                                                                "bg-tertiary text-brand-650 font-bold",
                                                        )}
                                                    >
                                                        <Sidebar.ItemLabel
                                                            title={dr.name}
                                                        />
                                                        <Sidebar.ItemBox>
                                                            {isDisclosureRequirementValid(
                                                                dr.cmsId,
                                                            ) ? (
                                                                <Icon
                                                                    name="check"
                                                                    className="text-brand-650"
                                                                />
                                                            ) : (
                                                                <Text
                                                                    whitespace="nowrap"
                                                                    variant="body-sm"
                                                                    className={cn(
                                                                        "text-neutral-400",
                                                                        isSelected &&
                                                                            "text-brand-650",
                                                                    )}
                                                                >
                                                                    {`${disclosureRequirementCompletionRatio(
                                                                        dr.cmsId,
                                                                    )} %`}
                                                                </Text>
                                                            )}
                                                        </Sidebar.ItemBox>
                                                    </FlexRow>
                                                )}
                                            </Sidebar.Item>
                                        ))}
                                </FlexCol>
                                {!isLastEsrs(
                                    idx,
                                    (pillar?.esrss || []) as CsrdEsrs[],
                                ) && <Sidebar.Divider className="mb-4" />}
                            </Sidebar.Group>
                        ),
                    )}
                </>
            </Sidebar.Content>
        </Sidebar>
    );
};
